import { useQuery } from '@apollo/client';
import { isBefore, isToday } from 'date-fns';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import Radio from '../../../../../components/form/Radio';
import Autocomplete from '../../../../../components/form/autocomplete';
import Button from '../../../../../components/form/button';
import Checkbox from '../../../../../components/form/checkbox';
import DatePicker from '../../../../../components/form/date';
import Select from '../../../../../components/form/select';
import ModalConfirm from '../../../../../components/modal/confirm';
import ClinicianOption from '../../../../../components/select-options/clinician';
import useCourses from '../../../../../graphql/hooks/queries/useCourses';
import useProtocols from '../../../../../graphql/hooks/queries/useProtocols';
import { LIST_CLINICIANS_QUERY } from '../../../../../graphql/query/clinicians';
import { ClinicianView, ISelectOption, SelectOptionClinician } from '../../../../../types';
import { TREATMENT_TYPE } from '../patient';
import styles from './styles.module.css';

const onFilterClinician =
  (filter: string) =>
  (option: ISelectOption<ClinicianView>): boolean => {
    const lc = filter.toLowerCase();
    const { name, email } = option.data as ClinicianView;
    return name.toLowerCase().includes(lc) || email.toLowerCase().includes(lc);
  };

type CourseProps = {
  onCancel: () => void;
  onConfirm: (payload: {
    courseId: string;
    startDate: Date;
    sendSms: boolean;
    clinicianId?: string;
    treatmentType: TREATMENT_TYPE;
  }) => void;
  existingClinicianId?: string;
};

export const Course = ({ onCancel, onConfirm, existingClinicianId }: CourseProps) => {
  const { courseOptions } = useCourses();
  const { protocolOptions } = useProtocols();
  const [courseId, setCourseId] = useState<string | undefined>();
  const [clinicianId, setClinicianId] = useState<string | undefined>();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [clinicians, setClinicians] = useState<SelectOptionClinician[]>([]);
  const [isOpenConfirmCourseStartDate, setIsOpenConfirmCourseStartDate] = useState(false);
  const [selectedTreatmentType, setSelectedTreatmentType] = useState<TREATMENT_TYPE>(
    TREATMENT_TYPE.PREPARATION_FOR_THERAPY
  );
  const { data: clinicianData, loading } = useQuery<{ listClinicians: ClinicianView[] }>(
    LIST_CLINICIANS_QUERY
  );
  const [sendSms, setSendSms] = useState(false);

  const handleSetSendSms = (e: FormEvent<HTMLInputElement>) => {
    setSendSms(e.currentTarget.checked);
  };

  const handleCancelCourseStartDateInPast = () => {
    // user has not confirmed the course start date in the past, reset the date
    setStartDate(null);
    setIsOpenConfirmCourseStartDate(false);
  };

  const handleConfirmCourseStartDateInPast = () => {
    setIsOpenConfirmCourseStartDate(false);
  };

  const handleSetStartDate = (date: Date | null) => {
    setStartDate(date);
    if (date && (isToday(date) || isBefore(date, new Date()))) {
      setIsOpenConfirmCourseStartDate(true);
    }
  };

  const handleTreatmentTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedTreatmentType(event.target.value as TREATMENT_TYPE);
    setCourseId(undefined);
  };

  // set up clinicians for autocomplete
  useEffect(() => {
    if (clinicianData)
      setClinicians(
        clinicianData.listClinicians.map(c => ({
          value: c.id.toString(),
          label: c.name,
          data: c,
        }))
      );
  }, [clinicianData]);

  // Auto populating clinician select field if there is one
  useEffect(() => {
    if (existingClinicianId && clinicians.length > 0) {
      setClinicianId(clinicians.find(clinician => clinician.value === existingClinicianId)?.value);
    }
  }, [existingClinicianId, clinicians]);

  const handleSelectClinician = (option: ISelectOption<ClinicianView> | null) => {
    setClinicianId(option ? (option.data as ClinicianView).id : undefined);
  };

  const courseSelectOptions =
    selectedTreatmentType === TREATMENT_TYPE.ONE_TO_ONE_THERAPY ? protocolOptions : courseOptions;

  const handleSubmit = async () => {
    if (!!courseId && !!startDate) {
      onConfirm({
        courseId,
        startDate,
        sendSms,
        clinicianId,
        treatmentType: selectedTreatmentType,
      });
    }
    setCourseId('');
  };

  return (
    <>
      <div className={styles.leftBorder}>&nbsp;</div>
      <div className={styles.wrapper}>
        <div className={styles.treatmentTypeContainer}>
          <p className={styles.treatmentTypeTitle}>Treatment Type</p>
          <div className={styles.treatmentTypeOptionsContainer}>
            <Radio
              label="Preparation for Therapy"
              value={TREATMENT_TYPE.PREPARATION_FOR_THERAPY}
              checked={selectedTreatmentType === TREATMENT_TYPE.PREPARATION_FOR_THERAPY}
              onChange={handleTreatmentTypeChange}
            />
            <Radio
              label="Group Therapy"
              value={TREATMENT_TYPE.GROUP_THERAPY}
              checked={selectedTreatmentType === TREATMENT_TYPE.GROUP_THERAPY}
              onChange={handleTreatmentTypeChange}
            />
            <Radio
              label="1:1 Therapy"
              value={TREATMENT_TYPE.ONE_TO_ONE_THERAPY}
              checked={selectedTreatmentType === TREATMENT_TYPE.ONE_TO_ONE_THERAPY}
              onChange={handleTreatmentTypeChange}
            />
          </div>
        </div>
        <div className={styles.formContainer}>
          <div className={styles.item}>
            <Select.Field
              label="Choose a course or protocol"
              value={courseId}
              required
              options={courseSelectOptions || []}
              onChange={e => setCourseId(e.target.value)}
              fullwidth
              usePlaceholderChild
            />
          </div>

          <div className={styles.item}>
            <DatePicker.Field
              label="Start Date"
              required
              selected={startDate}
              onChange={handleSetStartDate}
              fullwidth
            />
          </div>
          <div className={styles.item}>
            <Autocomplete.Field
              label="Assigned Clinician"
              placeholder="Search for name or email"
              options={clinicians}
              onSelect={handleSelectClinician}
              filterOn={onFilterClinician}
              value={existingClinicianId}
              disabled={loading}
              RenderOption={ClinicianOption}
              required={selectedTreatmentType === TREATMENT_TYPE.ONE_TO_ONE_THERAPY}
            />
          </div>
        </div>

        <div className="inner">
          <Checkbox
            label="Send SMS/Email invitation to patient now"
            checked={sendSms}
            onChange={handleSetSendSms}
          />
        </div>

        <div className={styles.inner}>
          <div className="buttons">
            <Button onClick={onCancel} className={styles.cancelButton}>
              Cancel
            </Button>

            <Button
              variant="dark"
              onClick={() => {
                handleSubmit();
              }}
              disabled={
                !courseId ||
                !startDate ||
                (selectedTreatmentType === TREATMENT_TYPE.ONE_TO_ONE_THERAPY && !clinicianId)
              }
              dot="green"
              className={styles.confirmButton}>
              Confirm
            </Button>
          </div>
        </div>
      </div>

      <ModalConfirm
        title="Are you sure the date is correct?"
        isOpen={isOpenConfirmCourseStartDate}
        onClose={handleCancelCourseStartDateInPast}
        onConfirm={handleConfirmCourseStartDateInPast}
        confirmText="Yes, it is correct">
        <p className={styles.modalBodyText}>
          You have selected a course start date in the past. Please keep in mind the following:
          <ul className={styles.list}>
            <li>
              All notifications relative to the start date which would have been sent in the past
              will not be sent.
            </li>
            <li>
              Any activities with an expiration date that have already expired may not be accessible
              to the patient for completion.
            </li>
          </ul>
        </p>
      </ModalConfirm>
    </>
  );
};

export default Course;
